import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Search, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown, Smartphone } from "lucide-react";
import { AdminShell } from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PLANS, type PlanId } from "@/lib/plans";
import { adminConsentSummary, adminListUsersPaged } from "@/lib/admin.functions";
import { TERMS_VERSION } from "@/lib/legal-versions";
import { DateCell } from "@/components/admin/DateCell";
import { ProviderBadge, RoleBadges } from "@/components/admin/ProviderBadge";
import { DeletionRequests } from "@/components/admin/DeletionRequests";

import { Link } from "@tanstack/react-router";

export const Route = createFileRoute("/_authenticated/admin/anvandare")({
  head: () => ({
    meta: [
      { title: "Användare — Administration | Sellavo" },
      { name: "description", content: "Hantera Sellavos användare, roller och kontotyper." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <AdminShell title="Användare">
      <UsersBody />
    </AdminShell>
  ),
});

type AdminUser = {
  id: string;
  email: string;
  display_name?: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  confirmed: boolean;
  roles: string[];
  plan: string;
  current_period_end: string | null;
  ads_count: number;
  consent_terms_version?: string | null;
  consent_accepted_at?: string | null;
  paid?: boolean;
  plan_source?: string | null;
  billing?: string | null;
  providers?: string[];
  devices?: { platforms: string[]; last_seen_at: string | null; has_app: boolean };
};

/** Kolumner som databasen kan sortera på. */
type SortKey =
  | "display_name"
  | "email"
  | "provider"
  | "plan"
  | "ads_count"
  | "created_at"
  | "last_sign_in_at"
  | "consent";

function SortableTh({
  id,
  sort,
  dir,
  onSort,
  children,
}: {
  id: SortKey;
  sort: SortKey;
  dir: "asc" | "desc";
  onSort: (id: SortKey) => void;
  children: React.ReactNode;
}) {
  const active = sort === id;
  return (
    <th
      className="whitespace-nowrap px-3 py-2"
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onSort(id)}
        className={`inline-flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-foreground ${
          active ? "text-foreground" : ""
        }`}
      >
        {children}
        {active ? (
          dir === "asc" ? (
            <ArrowUp className="h-3.5 w-3.5" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
        )}
      </button>
    </th>
  );
}


function consentBadge(version: string | null | undefined) {
  const current = version === TERMS_VERSION;
  const tone = current
    ? "bg-primary/10 text-primary"
    : version
      ? "bg-amber-500/15 text-amber-600 dark:text-amber-400"
      : "bg-muted text-muted-foreground";
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold ${tone}`}>
      {current ? version : version ? `Gammal (${version})` : "Ej godkänt"}
    </span>
  );
}

const PAGE_SIZE = 25;

function planBadge(plan: string, end: string | null) {
  const expired = end ? new Date(end).getTime() < Date.now() : false;
  const label = PLANS[(plan as PlanId) in PLANS ? (plan as PlanId) : "free"].name;
  const tone =
    expired || plan === "free"
      ? "bg-muted text-muted-foreground"
      : plan === "pro"
        ? "bg-primary text-primary-foreground"
        : "bg-secondary text-secondary-foreground";
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold ${tone}`}>
      {label}
      {expired ? " (utgången)" : ""}
    </span>
  );
}

/**
 * Visar om kontot faktiskt betalar. En nivå vi själva har lagt till i
 * administrationen är inget köp – då visas "Gåva från oss", aldrig ett pris.
 */
function payingBadge(
  plan: string,
  end: string | null,
  paid: boolean,
  source: string | null,
  billing: string | null,
) {
  const expired = end ? new Date(end).getTime() < Date.now() : false;
  const key: PlanId = (plan as PlanId) in PLANS ? (plan as PlanId) : "free";
  const def = PLANS[key];
  const chip = (tone: string, text: string) => (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold ${tone}`}>
      {text}
    </span>
  );

  if (key === "free") return chip("text-muted-foreground font-medium", "Gratis");
  if (expired) return chip("text-muted-foreground font-medium", "Utgången");
  if (!paid) {
    return chip(
      "bg-amber-500/15 text-amber-700 dark:text-amber-300",
      source === "admin" ? "Gåva från oss" : "Obetald",
    );
  }
  const price =
    billing === "yearly" ? (def.priceYearLabel ?? `${def.priceSek} kr/mån`) : def.priceLabel;
  return chip("bg-emerald-500/15 text-emerald-700 dark:text-emerald-300", `Betalande · ${price}`);
}


function UsersBody() {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortKey>("created_at");
  const [dir, setDir] = useState<"asc" | "desc">("desc");

  // Ny kolumn börjar med den mest användbara riktningen: text A–Ö, siffror och
  // datum med det senaste/största först.
  function changeSort(id: SortKey) {
    setPage(1);
    if (id === sort) {
      setDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSort(id);
    setDir(id === "display_name" || id === "email" || id === "provider" ? "asc" : "desc");
  }

  const fetchUsers = useServerFn(adminListUsersPaged);

  const query = useQuery({
    queryKey: ["admin-users-paged", search, page, sort, dir],
    queryFn: () =>
      fetchUsers({ data: { search: search || undefined, page, pageSize: PAGE_SIZE, sort, dir } }),
    placeholderData: keepPreviousData,
  });

  const total = query.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fetchConsentSummary = useServerFn(adminConsentSummary);
  const consentSummary = useQuery({
    queryKey: ["admin-consent-summary"],
    queryFn: () => fetchConsentSummary({}),
    staleTime: 60 * 1000,
  });

  return (
    <div className="space-y-6">
      <DeletionRequests />

      {consentSummary.data && (
        <div className="rounded-md border border-border bg-muted/30 px-4 py-3 text-sm">
          <span className="font-semibold text-foreground">
            {consentSummary.data.accepted.toLocaleString("sv-SE")} av{" "}
            {consentSummary.data.total.toLocaleString("sv-SE")}
          </span>{" "}
          <span className="text-muted-foreground">
            användare har godkänt aktuella villkor (version {consentSummary.data.version}). Övriga
            får en ruta med godkännande nästa gång de loggar in.
          </span>
        </div>
      )}
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setSearch(searchInput.trim());
        }}
      >
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Sök namn eller e-post"
            className="h-10 w-64 pl-9"
          />
        </div>
        <Button type="submit" size="sm" className="h-10">
          Sök
        </Button>
        {search && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-10"
            onClick={() => {
              setSearchInput("");
              setSearch("");
              setPage(1);
            }}
          >
            Rensa
          </Button>
        )}
        <p className="ml-auto text-sm text-muted-foreground">
          {query.isFetching ? "Uppdaterar…" : `${total.toLocaleString("sv-SE")} användare`}
        </p>
      </form>

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <SortableTh id="display_name" sort={sort} dir={dir} onSort={changeSort}>
                Användarnamn
              </SortableTh>
              <SortableTh id="email" sort={sort} dir={dir} onSort={changeSort}>
                E-post
              </SortableTh>
              <SortableTh id="provider" sort={sort} dir={dir} onSort={changeSort}>
                Inloggningssätt
              </SortableTh>
              <SortableTh id="plan" sort={sort} dir={dir} onSort={changeSort}>
                Kontotyp
              </SortableTh>
              <th className="whitespace-nowrap px-3 py-2">Betalning</th>
              <th className="whitespace-nowrap px-3 py-2">Förnyas</th>

              <th className="whitespace-nowrap px-3 py-2">Enheter</th>
              <th className="whitespace-nowrap px-3 py-2">Roll</th>
              <SortableTh id="ads_count" sort={sort} dir={dir} onSort={changeSort}>
                Annonser
              </SortableTh>
              <SortableTh id="created_at" sort={sort} dir={dir} onSort={changeSort}>
                Registrerad
              </SortableTh>
              <SortableTh id="last_sign_in_at" sort={sort} dir={dir} onSort={changeSort}>
                Senast inloggad
              </SortableTh>
              <SortableTh id="consent" sort={sort} dir={dir} onSort={changeSort}>
                Villkor
              </SortableTh>
              <th className="whitespace-nowrap px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {(query.data?.users ?? []).map((u: AdminUser) => (
              <tr key={u.id} className="border-t border-border transition-colors hover:bg-muted/50">
                <td className="whitespace-nowrap px-3 py-2">
                  <Link
                    to="/admin/anvandare/$userId"
                    params={{ userId: u.id }}
                    className="font-medium text-foreground hover:underline"
                  >
                    {u.display_name || "–"}
                  </Link>
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  {u.email}
                  {!u.confirmed && (
                    <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                      obekräftad
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  <ProviderBadge provider={(u.providers ?? []).join(", ")} />
                </td>
                <td className="whitespace-nowrap px-3 py-2">{planBadge(u.plan, u.current_period_end)}</td>
                <td className="whitespace-nowrap px-3 py-2">{payingBadge(u.plan, u.current_period_end, !!u.paid, u.plan_source ?? null, u.billing ?? null)}</td>

                <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                  {u.plan === "free" ? (
                    "–"
                  ) : u.current_period_end ? (
                    <span
                      className={
                        new Date(u.current_period_end).getTime() < Date.now()
                          ? "text-destructive"
                          : undefined
                      }
                    >
                      <DateCell value={u.current_period_end} />
                    </span>
                  ) : (
                    "Tills vidare"
                  )}
                </td>

                <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                  {u.devices && u.devices.platforms.length > 0 ? (
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-1">
                        {u.devices.has_app && (
                          <span className="inline-flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-semibold text-primary">
                            <Smartphone className="h-3 w-3" /> App
                          </span>
                        )}
                        <span className="text-xs capitalize">{u.devices.platforms.join(", ")}</span>
                      </div>
                      {u.devices.last_seen_at && (
                        <span className="text-[11px]">
                          <DateCell value={u.devices.last_seen_at} />
                        </span>
                      )}
                    </div>
                  ) : (
                    "–"
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                  <RoleBadges roles={u.roles} />
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">{u.ads_count}</td>
                <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                  <DateCell value={u.created_at} />
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                  <DateCell value={u.last_sign_in_at} />
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                  <div className="flex flex-col gap-0.5">
                    {consentBadge(u.consent_terms_version)}
                    {u.consent_accepted_at && (
                      <span className="text-[11px]">
                        <DateCell value={u.consent_accepted_at} />
                      </span>
                    )}
                  </div>
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right">
                  <div className="flex justify-end gap-2">
                    <Link
                      to="/admin/anvandare/$userId"
                      params={{ userId: u.id }}
                      className="inline-flex items-center rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent"
                    >
                      Öppna
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
            {query.data && query.data.users.length === 0 && (
              <tr>
                <td colSpan={13} className="px-3 py-8 text-center text-muted-foreground">
                  Inga användare matchade sökningen.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-3">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
        >
          <ChevronLeft className="mr-1 h-4 w-4" /> Föregående
        </Button>
        <p className="text-sm text-muted-foreground">
          Sida {page} av {pages}
        </p>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= pages}
          onClick={() => setPage((p) => p + 1)}
        >
          Nästa <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </div>

    </div>
  );
}
